import { clsx } from 'clsx'
import type { ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { toast } from 'sonner'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string, monthFormat: 'long' | 'short' = 'short'): string {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: monthFormat,
      day: 'numeric',
    })
  }
  catch {
    return dateString
  }
}

export function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const isThisYear = date.getFullYear() === now.getFullYear()
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    }
    
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    }
    
    if (!isThisYear) {
      dateOptions.year = '2-digit'
    }
    
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    
    return `${date.toLocaleDateString('en-US', dateOptions)} ${timeStr}`
  }
  catch {
    return dateString
  }
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price)
}

export function truncateText(text: string, maxLength: number = 15): string {
  if (text.length <= maxLength)
    return text
  return `${text.substring(0, maxLength)}...`
}

export function copyToClipboard(text: string, successMessage?: string): Promise<void> {
  const copyPromise = navigator.clipboard && window.isSecureContext
    ? navigator.clipboard.writeText(text)
    : (() => {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        return new Promise<void>((resolve, reject) => {
          document.execCommand('copy') ? resolve() : reject()
          textArea.remove()
        })
      })()

  return copyPromise
    .then(() => {
      toast.success(successMessage || 'Copied to clipboard', {
        icon: '✓',
        style: {
          color: 'rgb(34, 197, 94)', // green-500
        },
      })
    })
    .catch((err) => {
      toast.error('Failed to copy to clipboard', {
        icon: '✗',
        style: {
          color: 'rgb(239, 68, 68)', // red-500
        },
      })
      throw err
    })
}
